import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, Search, FileText, Download, AlertTriangle,
  Shield, Printer, Edit, Mail, X, Phone, MessageSquare, Check
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import DisclaimerModal from '../components/DisclaimerModal';
import TranslatePanel from '../components/TranslatePanel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const InformedConsentLibraryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // When mounted at /compliance-forms-library, show ONLY the two refusal forms
  // and rebrand the page header. Otherwise behave exactly as the original
  // Informed Consent Library (84 informed consents only).
  const isComplianceMode = location.pathname.startsWith('/compliance-forms-library');
  const { toast } = useToast();
  const { practice, user, isAuthenticated } = useAuth();
  
  const [consents, setConsents] = useState([]);
  const [filteredConsents, setFilteredConsents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailConsent, setEmailConsent] = useState(null);
  const [patientEmail, setPatientEmail] = useState('');
  const [patientName, setPatientName] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityHistory, setActivityHistory] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [patients, setPatients] = useState([]);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  // SMS state
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsConsent, setSmsConsent] = useState(null);
  const [smsPatientSearchQuery, setSmsPatientSearchQuery] = useState('');
  const [filteredSmsPatients, setFilteredSmsPatients] = useState([]);
  const [smsPatient, setSmsPatient] = useState(null);
  const [sendingSMS, setSendingSMS] = useState(false);

  // PDF blob cache
  const pdfCacheRef = useRef({});

  // Translation state
  const [translatedConsentText, setTranslatedConsentText] = useState(null);
  const [consentTranslationLang, setConsentTranslationLang] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const checkAuth = async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const token = localStorage.getItem('dentalToken');
      console.log('🔍 Checking authentication - token exists:', !!token);
      
      if (!token) {
        console.log('⚠️ No token found after delay, redirecting...');
        navigate('/');
        return;
      }
      
      // DISCLAIMER REMOVED - Load consents directly
      setDisclaimerAccepted(true);
      fetchConsents();
      fetchPatients();
    };
    
    checkAuth();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPatientDropdown && !event.target.closest('.patient-search-container')) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPatientDropdown]);

  // DISCLAIMER REMOVED - No longer needed

  useEffect(() => {
    // Filter patients based on search query
    const query = patientSearchQuery.toLowerCase().trim();
    
    if (!query) {
      setFilteredPatients([]);
      return;
    }
    
    const filtered = patients.filter(patient => {
      const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
      const email = (patient.email || '').toLowerCase();
      
      return fullName.includes(query) || email.includes(query);
    });
    
    // Sort by relevance - exact matches first
    filtered.sort((a, b) => {
      const aName = `${a.firstName} ${a.lastName}`.toLowerCase();
      const bName = `${b.firstName} ${b.lastName}`.toLowerCase();
      const aEmail = (a.email || '').toLowerCase();
      const bEmail = (b.email || '').toLowerCase();
      
      // Exact name match
      if (aName === query && bName !== query) return -1;
      if (bName === query && aName !== query) return 1;
      
      // Name starts with query
      if (aName.startsWith(query) && !bName.startsWith(query)) return -1;
      if (bName.startsWith(query) && !aName.startsWith(query)) return 1;
      
      // Email starts with query
      if (aEmail.startsWith(query) && !bEmail.startsWith(query)) return -1;
      if (bEmail.startsWith(query) && !aEmail.startsWith(query)) return 1;
      
      return aName.localeCompare(bName);
    });
    
    setFilteredPatients(filtered.slice(0, 10)); // Limit to 10 results
  }, [patientSearchQuery, patients]);

  // Filter patients for SMS modal - only show patients with phone numbers
  useEffect(() => {
    let filtered = [...patients];

    // First filter to only patients with phone numbers
    filtered = filtered.filter(patient => patient.cellPhone || patient.phone);

    if (smsPatientSearchQuery.trim()) {
      const searchLower = smsPatientSearchQuery.toLowerCase();
      filtered = filtered.filter(patient =>
        `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchLower) ||
        (patient.email && patient.email.toLowerCase().includes(searchLower))
      );
    }

    filtered.sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    });

    setFilteredSmsPatients(filtered);
  }, [smsPatientSearchQuery, patients]);

  useEffect(() => {
    const searchLower = searchQuery.toLowerCase().trim();
    
    if (!searchLower) {
      setFilteredConsents(consents);
      return;
    }
    
    const filtered = consents.filter(consent => {
      const name = consent.name.toLowerCase();
      const category = (consent.category || '').toLowerCase();
      
      return name.includes(searchLower) || category.includes(searchLower);
    });
    
    filtered.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      
      if (aName.includes(searchLower) && !bName.includes(searchLower)) return -1;
      if (!aName.includes(searchLower) && bName.includes(searchLower)) return 1;
      
      return aName.localeCompare(bName);
    });
    
    setFilteredConsents(filtered);
  }, [searchQuery, consents]);

  const checkDisclaimerStatus = async () => {
    console.log('🔍 checkDisclaimerStatus called');
    try {
      const token = localStorage.getItem('dentalToken');
      console.log('🔑 Token exists:', !!token);
      if (!token) {
        console.log('❌ No token, returning');
        return;
      }

      console.log('📡 Calling API:', `${backendUrl}/api/informed-consent/disclaimer/status`);
      const response = await axios.get(
        `${backendUrl}/api/informed-consent/disclaimer/status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('📥 API Response:', response.data);
      console.log('✓ Accepted status:', response.data.accepted);

      if (response.data.accepted) {
        console.log('✅ Disclaimer already accepted - HIDING modal');
        setDisclaimerAccepted(true);
        setShowDisclaimer(false);
        fetchConsents();
      } else {
        console.log('⚠️ Disclaimer not yet accepted - SHOWING modal');
        setShowDisclaimer(true);
      }
    } catch (error) {
      console.error('❌ Error checking disclaimer status:', error);
      console.error('Error details:', error.response?.data);
      // If error, show disclaimer to be safe
      console.log('🚨 Error occurred - SHOWING modal as fallback');
      setShowDisclaimer(true);
    }
  };

  const fetchConsents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dentalToken');
      
      if (!token) {
        navigate('/');
        return;
      }
      
      const response = await axios.get(
        `${backendUrl}/api/informed-consent/list`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        const all = response.data.data || [];
        // /informed-consent-library: show only standard informed consents.
        // /compliance-forms-library: show only the refusal compliance forms.
        const filtered = isComplianceMode
          ? all.filter(c => c.isRefusalForm || c.isXrayRefusalForm || c.isPerioRefusalForm || c.isFailureToCompleteForm || c.isPatientComplianceForm)
          : all.filter(c => !c.isRefusalForm && !c.isXrayRefusalForm && !c.isPerioRefusalForm && !c.isFailureToCompleteForm && !c.isPatientComplianceForm);
        setConsents(filtered);
        setFilteredConsents(filtered);
      }
    } catch (error) {
      console.error('Fetch consents error:', error);
      
      if (error.response?.status === 401) {
        navigate('/');
        return;
      }
      
      toast({
        title: "Error",
        description: "Failed to load informed consents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('dentalToken');
      
      const response = await axios.get(
        `${backendUrl}/api/practice/patients`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setPatients(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  };

  const handleDisclaimerAccept = () => {
    // DisclaimerModal already saved to backend before calling this
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
    toast({
      title: "Disclaimer Accepted",
      description: "You can now access informed consent documents",
    });
  };

  const handleConsentSelect = async (consent) => {
    try {
      // Fetch detailed consent data with cleaned sections
      const token = localStorage.getItem('dentalToken');
      const response = await axios.get(
        `${backendUrl}/api/informed-consent/${consent.id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setSelectedConsent(response.data.data);
      } else {
        // Fallback to list data if detail fetch fails
        setSelectedConsent(consent);
      }
    } catch (error) {
      console.error('Failed to fetch consent detail, using list data:', error);
      setSelectedConsent(consent);
    }
    setShowPreview(true);
  };

  const handleDownloadPDF = async (consent) => {
    try {
      toast({
        title: "Creating PDF",
        description: "Generating PDF document...",
      });

      const cacheKey = `ic_${consent.id}`;
      let blob = pdfCacheRef.current[cacheKey];
      
      if (!blob) {
        const token = localStorage.getItem('dentalToken');
        const response = await axios.post(
          `${backendUrl}/api/informed-consent/generate-pdf`,
          { consentId: consent.id },
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        
        blob = new Blob([response.data], { type: 'application/pdf' });
        pdfCacheRef.current[cacheKey] = blob;
        setTimeout(() => { delete pdfCacheRef.current[cacheKey]; }, 300000);
      }
      
      const url = window.URL.createObjectURL(blob);
      
      const safeName = consent.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = `${safeName}_consent.pdf`;
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: `PDF downloaded: ${consent.name}`,
      });
      
    } catch (error) {
      console.error('PDF download error:', error);
      toast({
        title: "Error",
        description: `Failed to download PDF: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    }
  };

  const handlePrintPDF = async (consent) => {
    try {
      toast({
        title: "Preparing to Print",
        description: "Loading PDF for printing...",
      });

      const cacheKey = `ic_${consent.id}`;
      let blob = pdfCacheRef.current[cacheKey];
      
      if (!blob) {
        const token = localStorage.getItem('dentalToken');
        const response = await axios.post(
          `${backendUrl}/api/informed-consent/generate-pdf`,
          { consentId: consent.id },
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        
        blob = new Blob([response.data], { type: 'application/pdf' });
        pdfCacheRef.current[cacheKey] = blob;
        setTimeout(() => { delete pdfCacheRef.current[cacheKey]; }, 300000);
      }
      
      const url = URL.createObjectURL(blob);
      
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;
      
      document.body.appendChild(iframe);
      
      iframe.onload = function() {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          
          toast({
            title: "Print Dialog Opened",
            description: "Complete printing, then this will auto-cleanup",
            duration: 5000,
          });
          
          const cleanup = () => {
            try {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe);
              }
              URL.revokeObjectURL(url);
            } catch (e) {
              console.error('Cleanup error:', e);
            }
          };
          
          setTimeout(cleanup, 120000);
          
          window.addEventListener('focus', function focusHandler() {
            setTimeout(() => {
              cleanup();
              window.removeEventListener('focus', focusHandler);
            }, 1000);
          }, { once: true });
          
        } catch (e) {
          console.error('Print error:', e);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${consent.name.replace(/[^a-z0-9]/gi, '_')}_Consent.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
          
          toast({
            title: "Print Not Supported",
            description: "PDF downloaded instead. Open it to print.",
            duration: 3000,
          });
        }
      };
      
    } catch (error) {
      console.error('PDF print error:', error);
      toast({
        title: "Error",
        description: `Failed to generate PDF: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleEditConsent = (consent) => {
    setSelectedConsent(consent);
    setShowPreview(false);
    // Navigate to edit page with consent data
    navigate(`/edit-informed-consent/${consent.id}`, { state: { consent } });
  };


  const handleEmailConsent = (consent) => {
    setEmailConsent(consent);
    setPatientEmail('');
    setPatientName('');
    setShowEmailModal(true);
    setPatientSearchQuery('');
    setSelectedPatient(null);
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setPatientEmail(patient.email || '');
    setPatientName(`${patient.firstName} ${patient.lastName}`);
    setPatientSearchQuery(`${patient.firstName} ${patient.lastName}`);
    setShowPatientDropdown(false);
  };

  const sendEmail = async () => {
    if (!patientEmail.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter a patient email address",
        variant: "destructive",
      });
      return;
    }

    try {
      setSendingEmail(true);
      const token = localStorage.getItem('dentalToken');
      
      await axios.post(
        `${backendUrl}/api/informed-consent/email`,
        {
          patientEmail: patientEmail.trim(),
          patientName: patientName.trim(),
          consentId: emailConsent.id,
          consentName: emailConsent.name
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast({
        title: "Email Sent",
        description: `Informed consent sent to ${patientEmail}`,
      });
      
      setShowEmailModal(false);
      setPatientEmail('');
      setPatientName('');
      setEmailConsent(null);
      
    } catch (error) {
      console.error('Email error:', error);
      toast({
        title: "Error",
        description: `Failed to send email: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // SMS Functions
  const handleTextConsent = (consent) => {
    setSmsConsent(consent);
    setSmsPatient(null);
    setSmsPatientSearchQuery('');
    setShowSmsModal(true);
  };

  const sendSMS = async () => {
    if (!smsPatient) {
      toast({
        title: "Patient Required",
        description: "Please select a patient to send the text",
        variant: "destructive",
      });
      return;
    }

    const phoneNumber = smsPatient.cellPhone || smsPatient.phone;
    if (!phoneNumber) {
      toast({
        title: "No Phone Number",
        description: "Selected patient does not have a phone number",
        variant: "destructive",
      });
      return;
    }

    try {
      setSendingSMS(true);
      const token = localStorage.getItem('dentalToken');
      const patientName = `${smsPatient.firstName} ${smsPatient.lastName}`;
      
      const response = await fetch(`${backendUrl}/api/sms/send-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient_id: smsPatient.id,
          document_type: 'consent',
          document_id: smsConsent.id,
          phone_number: phoneNumber
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Failed to send SMS');
      }

      toast({
        title: "Text Sent!",
        description: `Informed consent "${smsConsent.name}" texted to ${patientName}`,
      });

      setShowSmsModal(false);
      setSmsConsent(null);
      setSmsPatient(null);
      setSmsPatientSearchQuery('');
      
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send text. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSendingSMS(false);
    }
  };

  const handleExportActivity = async () => {
    try {
      setExportingData(true);
      const token = localStorage.getItem('dentalToken');
      
      const response = await axios.get(
        `${backendUrl}/api/informed-consent/export-activity`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informed_consent_activity_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Export Successful",
        description: "Activity log has been downloaded",
      });
      
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Error",
        description: `Failed to export activity log: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    } finally {
      setExportingData(false);
    }
  };

  const handleExportAssignments = async () => {
    try {
      setExportingData(true);
      const token = localStorage.getItem('dentalToken');
      
      const response = await axios.get(
        `${backendUrl}/api/informed-consent/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informed_consent_assignments_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Export Successful",
        description: "Informed consent assignments have been downloaded",
      });
      
    } catch (error) {
      console.error('Export assignments error:', error);
      toast({
        title: "Error",
        description: `Failed to export assignments: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    } finally {
      setExportingData(false);
    }
  };

  const handleViewActivity = async () => {
    try {
      setLoadingActivity(true);
      setShowActivityModal(true);
      const token = localStorage.getItem('dentalToken');
      
      const response = await axios.get(
        `${backendUrl}/api/informed-consent/activity-history`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 100 }
        }
      );
      
      setActivityHistory(response.data.data || []);
      
    } catch (error) {
      console.error('Activity history error:', error);
      toast({
        title: "Error",
        description: `Failed to load activity history: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleResendEmail = async (consent, email) => {
    try {
      setSendingEmail(true);
      const token = localStorage.getItem('dentalToken');
      
      await axios.post(
        `${backendUrl}/api/informed-consent/resend-email`,
        {
          patientEmail: email,
          consentId: consent.id,
          consentName: consent.name
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast({
        title: "Email Resent",
        description: `Informed consent resent to ${email}`,
      });
      
      // Refresh activity history if modal is open
      if (showActivityModal) {
        handleViewActivity();
      }
      
    } catch (error) {
      console.error('Resend error:', error);
      toast({
        title: "Error",
        description: `Failed to resend email: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    } finally {
      setSendingEmail(false);
    }
  };


  const getCategoryColor = (category) => {
    const colors = {
      'oral-surgery': 'bg-red-100 text-red-800',
      'implantology': 'bg-purple-100 text-purple-800',
      'bone-grafting': 'bg-orange-100 text-orange-800',
      'periodontics': 'bg-green-100 text-green-800',
      'endodontics': 'bg-blue-100 text-blue-800',
      'prosthodontics': 'bg-pink-100 text-pink-800',
      'orthodontics': 'bg-teal-100 text-teal-800',
      'general-dentistry': 'bg-gray-100 text-gray-800',
      'oral-maxillofacial': 'bg-indigo-100 text-indigo-800',
      'anesthesia': 'bg-yellow-100 text-yellow-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  console.log('🎨🎨🎨 RENDER - State:', { disclaimerAccepted, showDisclaimer, loading });
  console.log('🎨 Will show modal?', showDisclaimer);
  console.log('🎨 Disclaimer accepted?', disclaimerAccepted);
  
  // NO EARLY RETURN - Always render page with modal

  if (loading && disclaimerAccepted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading informed consents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* DISCLAIMER REMOVED */}

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/')}
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {isComplianceMode ? 'Compliance Forms Library' : 'Informed Consent Library'}
                </h1>
                <p className="text-gray-600">
                  {isComplianceMode
                    ? 'Compliance forms — refusals, acknowledgments, and incomplete-treatment notices'
                    : 'Browse and manage informed consent documents'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm text-gray-600">{practice?.name}</p>
                <p className="text-xs text-gray-500">Practice Administrator</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>Important:</strong> These are template documents. You must customize them for your practice and have them reviewed by your legal counsel before use.
          </AlertDescription>
        </Alert>
      </div>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder={isComplianceMode ? "Search compliance forms..." : "Search informed consents..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Consents Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredConsents.map((consent) => (
            <Card key={consent.id} className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{consent.name}</CardTitle>
                  <Badge className={getCategoryColor(consent.category)}>
                    {consent.category?.replace('-', ' ')}
                  </Badge>
                </div>
                {consent.isCustomized && (
                  <Badge variant="outline" className="mt-2">
                    <Shield className="h-3 w-3 mr-1" />
                    Customized
                  </Badge>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleConsentSelect(consent)}
                      className="flex-1"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditConsent(consent)}
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEmailConsent(consent)}
                      className="flex-1"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTextConsent(consent)}
                      className="flex-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Text
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePrintPDF(consent)}
                      className="flex-1"
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadPDF(consent)}
                      className="flex-1"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredConsents.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No consents found</h3>
            <p className="text-gray-600">
              {searchQuery ? 'Try adjusting your search terms' : 'No informed consents available'}
            </p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && selectedConsent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {selectedConsent.name}
                    {consentTranslationLang && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {consentTranslationLang}
                      </span>
                    )}
                  </h2>
                  <Badge className={getCategoryColor(selectedConsent.category)}>
                    {selectedConsent.category?.replace('-', ' ')}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <TranslatePanel
                    documentType="consent"
                    documentId={selectedConsent.id}
                    token={localStorage.getItem('dentalToken')}
                    compact={true}
                    autoTranslateLanguage={selectedPatient?.preferredLanguage || null}
                    onTranslated={(text, langCode, langName) => {
                      setTranslatedConsentText(text);
                      setConsentTranslationLang(langName);
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowPreview(false);
                      setTranslatedConsentText(null);
                      setConsentTranslationLang(null);
                    }}
                  >
                    ×
                  </Button>
                </div>
              </div>
              
              <div className="space-y-4">
                {translatedConsentText ? (
                  <div className="whitespace-pre-wrap text-gray-700 text-sm">
                    {translatedConsentText}
                  </div>
                ) : (
                  Object.entries(selectedConsent.sections || {}).map(([key, value]) => (
                    value && (
                      <div key={key}>
                        <h3 className="font-semibold mb-2 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</h3>
                        <p className="text-gray-700 whitespace-pre-wrap text-sm">{value}</p>
                      </div>
                    )
                  ))
                )}
              </div>
              
              <div className="flex gap-2 mt-6">
                <Button
                  onClick={() => handleDownloadPDF(selectedConsent)}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPreview(false);
                    setTranslatedConsentText(null);
                    setConsentTranslationLang(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && emailConsent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Email Informed Consent
            </h2>
            <p className="text-gray-600 mb-4">
              Send "{emailConsent.name}" to a patient
            </p>
            
            <div className="mb-4 relative patient-search-container">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Patient (by name or email)
              </label>
              <div className="relative">
                <Input
                  type="text"
                  value={patientSearchQuery}
                  onChange={(e) => {
                    setPatientSearchQuery(e.target.value);
                    setShowPatientDropdown(true);
                  }}
                  onFocus={() => patientSearchQuery && setShowPatientDropdown(true)}
                  placeholder="Type patient name or email..."
                  disabled={sendingEmail}
                  className="pr-10"
                />
                <Search className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
              </div>
              
              {/* Patient dropdown */}
              {showPatientDropdown && filteredPatients.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredPatients.map((patient) => (
                    <button
                      key={patient.id}
                      type="button"
                      onClick={() => handleSelectPatient(patient)}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="font-medium text-gray-900">
                        {patient.firstName} {patient.lastName}
                      </div>
                      <div className="text-sm text-gray-600">{patient.email}</div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Show selected patient info */}
              {selectedPatient && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-blue-900">
                        {selectedPatient.firstName} {selectedPatient.lastName}
                      </div>
                      <div className="text-sm text-blue-700">{selectedPatient.email}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedPatient(null);
                        setPatientEmail('');
                        setPatientName('');
                        setPatientSearchQuery('');
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Show message if no results */}
              {patientSearchQuery && filteredPatients.length === 0 && showPatientDropdown && (
                <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                  No patients found matching "{patientSearchQuery}"
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={sendEmail}
                disabled={sendingEmail || !patientEmail.trim()}
                className="flex-1"
              >
                <Mail className="h-4 w-4 mr-2" />
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowEmailModal(false);
                  setPatientEmail('');
                  setPatientName('');
                  setEmailConsent(null);
                }}
                disabled={sendingEmail}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Activity History Modal */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    Informed Consent Activity History
                  </h2>
                  <p className="text-sm text-gray-600">
                    Legal audit trail of all informed consent interactions
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowActivityModal(false)}
                >
                  ×
                </Button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {loadingActivity ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">Loading activity...</p>
                </div>
              ) : activityHistory.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No activity yet</h3>
                  <p className="text-gray-600">
                    Start sending informed consents to build your legal audit trail
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-7 gap-4 pb-2 border-b font-semibold text-sm text-gray-700">
                    <div>Date/Time</div>
                    <div className="col-span-2">Consent Name</div>
                    <div>Activity</div>
                    <div>Patient Email</div>
                    <div>Sent By</div>
                    <div>Actions</div>
                  </div>
                  {activityHistory.map((activity) => (
                    <div key={activity.id} className="grid grid-cols-7 gap-4 py-3 border-b text-sm items-center">
                      <div className="text-gray-600">
                        {new Date(activity.timestamp).toLocaleString()}
                      </div>
                      <div className="col-span-2 font-medium">{activity.consentName}</div>
                      <div>
                        <Badge className={
                          activity.activityType === 'email' ? 'bg-blue-100 text-blue-800' :
                          activity.activityType === 'resend' ? 'bg-purple-100 text-purple-800' :
                          activity.activityType === 'download' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {activity.activityType}
                        </Badge>
                      </div>
                      <div className="text-gray-700 truncate" title={activity.patientEmail}>
                        {activity.patientEmail || 'N/A'}
                      </div>
                      <div className="text-gray-600 text-xs">
                        {activity.sentByName || 'N/A'}
                      </div>
                      <div>
                        {activity.patientEmail && activity.activityType === 'email' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const consent = consents.find(c => c.id === activity.consentId);
                              if (consent) {
                                handleResendEmail(consent, activity.patientEmail);
                              }
                            }}
                            disabled={sendingEmail}
                            className="text-xs"
                          >
                            Resend
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t bg-gray-50">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  Total activities: {activityHistory.length}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleExportActivity}
                    disabled={exportingData}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Full Report
                  </Button>
                  <Button
                    onClick={() => setShowActivityModal(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SMS Modal */}
      <Dialog open={showSmsModal} onOpenChange={setShowSmsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-green-600" />
              Text Informed Consent to Patient
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a patient to text <span className="font-medium text-indigo-600">{smsConsent?.name}</span>
            </p>
            
            {/* Patient Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name or email..."
                value={smsPatientSearchQuery}
                onChange={(e) => setSmsPatientSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Patient List */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredSmsPatients.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <Phone className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium">
                    {smsPatientSearchQuery 
                      ? 'No patients with mobile numbers match your search' 
                      : 'No patients with mobile numbers available'}
                  </p>
                  <p className="text-xs mt-1">Only patients with phone numbers can receive text messages</p>
                </div>
              ) : (
                filteredSmsPatients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => setSmsPatient(patient)}
                    className={`p-3 border rounded-lg transition cursor-pointer ${
                      smsPatient?.id === patient.id
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-xs text-gray-600 flex items-center">
                          <Phone className="h-3 w-3 mr-1" />
                          {patient.cellPhone || patient.phone}
                        </p>
                      </div>
                      {smsPatient?.id === patient.id && (
                        <Check className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSmsModal(false);
                  setSmsConsent(null);
                  setSmsPatient(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={sendSMS}
                disabled={!smsPatient || sendingSMS}
                className="bg-green-600 hover:bg-green-700"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                {sendingSMS ? 'Sending...' : 'Send Text'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InformedConsentLibraryPage;
